import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { _ as _to_property_key } from "@swc/helpers/_/_to_property_key";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { Component } from 'react';
import { TableListContext } from './TableList.utils';
import { createTableListStore } from './TableListStore';
const configFromProps = (props)=>{
    var _props_variant;
    return {
        columnDefs: props.columnDefs,
        expandableRows: props.expandableRows,
        expandableRowsVariant: props.expandableRowsVariant,
        enableRowSelection: props.enableRowSelection,
        variant: (_props_variant = props.variant) !== null && _props_variant !== void 0 ? _props_variant : 'default',
        hasActionColumn: props.hasActionColumn,
        hPadding: props.hPadding || 'm',
        justifyBetween: props.justifyBetween || false,
        disableMobile: props.disableMobile
    };
};
const configChanged = (prev, next)=>prev.columnDefs !== next.columnDefs || prev.expandableRows !== next.expandableRows || prev.expandableRowsVariant !== next.expandableRowsVariant || prev.enableRowSelection !== next.enableRowSelection || prev.variant !== next.variant || prev.hasActionColumn !== next.hasActionColumn || prev.hPadding !== next.hPadding || prev.justifyBetween !== next.justifyBetween || prev.disableMobile !== next.disableMobile;
/**
 * Provides configuration to a non-grid table.
 * Implemented as a class component to promote stability even when callback props change their reference.
 */ export class TableListProvider extends Component {
    componentDidUpdate(prevProps) {
        const { sort, selectedRows, numRows, numSelectedRows, onToggleAllSelection } = this.props;
        const nextConfig = configFromProps(this.props);
        this.store.setState((s)=>{
            let next = s;
            const withChange = (partial)=>_object_spread({}, next, partial);
            if (configChanged(s.config, nextConfig)) {
                next = withChange({
                    config: nextConfig
                });
            }
            if (sort !== prevProps.sort) {
                next = withChange({
                    sort
                });
            }
            if (selectedRows !== prevProps.selectedRows && selectedRows) {
                next = withChange({
                    selectedRows
                });
            }
            if (numRows !== prevProps.numRows) {
                next = withChange({
                    numRows
                });
            }
            if (numSelectedRows !== prevProps.numSelectedRows) {
                next = withChange({
                    numSelectedRows
                });
            }
            if (onToggleAllSelection !== prevProps.onToggleAllSelection) {
                next = withChange({
                    onToggleAllSelection
                });
            }
            return next;
        });
    }
    render() {
        return /*#__PURE__*/ _jsx(TableListContext.Provider, {
            value: this.contextValue,
            children: this.props.children
        });
    }
    constructor(...args){
        super(...args), _define_property(this, "lastExpandAll", null), _define_property(this, "store", createTableListStore({
            config: configFromProps(this.props),
            expandedRows: this.props.initialExpandedRows || {},
            selectedRows: this.props.selectedRows || {},
            sort: this.props.sort,
            numRows: this.props.numRows,
            numSelectedRows: this.props.numSelectedRows,
            onToggleAllSelection: this.props.onToggleAllSelection
        })), _define_property(this, "registerRow", (param)=>{
            let { rowId, isExpanded } = param;
            this.store.setState((s)=>{
                var _this_lastExpandAll;
                return _object_spread_props(_object_spread({}, s), {
                    expandedRows: _object_spread_props(_object_spread({}, s.expandedRows), {
                        [rowId]: (_this_lastExpandAll = this.lastExpandAll) !== null && _this_lastExpandAll !== void 0 ? _this_lastExpandAll : isExpanded
                    })
                });
            });
        }), _define_property(this, "unregisterRow", (rowId)=>{
            this.store.setState((s)=>{
                const _s_expandedRows = s.expandedRows, { [rowId]: _ } = _s_expandedRows, remainingRows = _object_without_properties(_s_expandedRows, [
                    _to_property_key(rowId)
                ]);
                return _object_spread_props(_object_spread({}, s), {
                    expandedRows: remainingRows
                });
            });
        }), _define_property(this, "expandAllRows", (isAllExpanded)=>{
            this.lastExpandAll = isAllExpanded;
            this.store.setState((s)=>{
                const expandedRows = Object.keys(s.expandedRows).reduce((acc, key)=>{
                    acc[key] = isAllExpanded;
                    return acc;
                }, {});
                return _object_spread_props(_object_spread({}, s), {
                    expandedRows
                });
            });
        }), _define_property(this, "toggleExpandedRow", (rowId)=>{
            this.store.setState((s)=>{
                const rowExpanded = !s.expandedRows[rowId];
                const baseRows = this.props.singleExpandOnly && rowExpanded ? Object.keys(s.expandedRows).reduce((acc, key)=>{
                    acc[key] = false;
                    return acc;
                }, {}) : s.expandedRows;
                return _object_spread_props(_object_spread({}, s), {
                    expandedRows: _object_spread_props(_object_spread({}, baseRows), {
                        [rowId]: rowExpanded
                    })
                });
            });
        }), _define_property(this, "setSort", (newSort)=>{
            const { onSortChange } = this.props;
            const { sort } = this.store.getState();
            const dataChanged = (sort === null || sort === void 0 ? void 0 : sort.colIndex) !== (newSort === null || newSort === void 0 ? void 0 : newSort.colIndex) || (sort === null || sort === void 0 ? void 0 : sort.sort) !== (newSort === null || newSort === void 0 ? void 0 : newSort.sort);
            if (dataChanged) {
                onSortChange && onSortChange(newSort);
                this.store.setState((s)=>_object_spread_props(_object_spread({}, s), {
                        sort: newSort
                    }));
            }
        }), _define_property(this, "setSelectedRows", (changes)=>{
            const { onSelectionChange } = this.props;
            const { selectedRows } = this.store.getState();
            const changedIds = Object.keys(changes).filter((id)=>selectedRows[id] !== changes[id]);
            if (changedIds.length === 0) return;
            const newSelectedRows = _object_spread({}, selectedRows);
            changedIds.forEach((id)=>{
                newSelectedRows[id] = changes[id];
            });
            onSelectionChange && onSelectionChange(newSelectedRows, changes);
            this.store.setState((s)=>_object_spread_props(_object_spread({}, s), {
                    selectedRows: newSelectedRows
                }));
        }), _define_property(this, "contextValue", {
            store: this.store,
            actions: {
                toggleExpandedRow: this.toggleExpandedRow,
                setSelectedRows: this.setSelectedRows,
                setSort: this.setSort,
                expandAllRows: this.expandAllRows,
                registerRow: this.registerRow,
                unregisterRow: this.unregisterRow
            }
        });
    }
}
